import { useState, useMemo } from 'react';
import { NavLink, useLocation, Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { icons } from '@/components/ui/Icons';
import { type AdminNavItem } from '@/types/admin';
import { filterAdminNavigationByPermissions } from '@/config/adminNavigation';
import { useAuth } from '@/context/AuthContext';

interface AdminLayoutProps {
  navigation: AdminNavItem[];
}

export function AdminLayout({ navigation }: AdminLayoutProps) {
  const location = useLocation();
  const { permissions } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const filteredNavigation = useMemo(
    () => filterAdminNavigationByPermissions(navigation, permissions),
    [navigation, permissions]
  );

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isParentActive = (item: AdminNavItem) => {
    if (!item.children) return false;
    return item.children.some((child) => isActive(child.path));
  };

  const renderIcon = (name: string, className = 'h-4 w-4') => {
    const Icon = icons[name as keyof typeof icons];
    return Icon ? <Icon className={className} aria-hidden="true" /> : null;
  };

  const closeMenus = () => {
    setOpenSubmenu(null);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex">
      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-neutral-950/40 lg:hidden"
          onClick={closeMenus}
          aria-hidden="true"
        />
      )}

      {/* Admin Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out border-r border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        aria-label="Admin-Navigation"
      >
        <div className="flex h-full flex-col">
          {/* Sidebar Header */}
          <div className="flex h-16 items-center justify-between border-b border-neutral-200 px-4 dark:border-neutral-800">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary-600 text-white shadow-subtle">
                <icons.ShieldCheck className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="truncate text-sm font-semibold tracking-[0.01em] text-neutral-900 dark:text-neutral-100">
                Administration
              </span>
            </div>
            <button
              type="button"
              onClick={closeMenus}
              className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-neutral-600 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 lg:hidden"
              aria-label="Menü schließen"
              aria-expanded={sidebarOpen}
            >
              <icons.X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3" aria-label="Admin-Hauptnavigation">
            <ul className="space-y-1" role="list">
              {filteredNavigation.map((item) => (
                <AdminNavItem
                  key={item.id}
                  item={item}
                  isActive={isActive(item.path)}
                  isParentActive={isParentActive(item)}
                  openSubmenu={openSubmenu}
                  onToggleSubmenu={setOpenSubmenu}
                  onNavigate={closeMenus}
                  renderIcon={renderIcon}
                  isActivePath={isActive}
                />
              ))}
            </ul>
          </nav>

          {/* Sidebar Footer - Back to Klinikum */}
          <div className="border-t border-neutral-200 p-3 dark:border-neutral-800">
            <NavLink
              to="/"
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-primary-50 hover:text-primary-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
              onClick={closeMenus}
            >
              <icons.ArrowLeft className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span className="truncate">Zum Klinikbereich</span>
            </NavLink>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:ml-64">
        <div className="sticky top-0 z-30 border-b border-neutral-200 bg-white/80 backdrop-blur-sm dark:border-neutral-800 dark:bg-neutral-900/80 lg:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-neutral-600 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Admin-Menü öffnen"
              aria-expanded={sidebarOpen}
              aria-controls="admin-sidebar"
            >
              <icons.Menu className="h-5 w-5" />
            </button>
            <h1 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Administration</h1>
            <div className="w-9" />
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

interface AdminNavItemProps {
  item: AdminNavItem;
  isActive: boolean;
  isParentActive: boolean;
  openSubmenu: string | null;
  onToggleSubmenu: (path: string | null) => void;
  onNavigate: () => void;
  renderIcon: (name: string, className?: string) => React.ReactNode;
  isActivePath: (path: string) => boolean;
}

function AdminNavItem({
  item,
  isActive,
  isParentActive,
  openSubmenu,
  onToggleSubmenu,
  onNavigate,
  renderIcon,
  isActivePath,
}: AdminNavItemProps) {
  const hasChildren = item.children && item.children.length > 0;
  const isOpen = openSubmenu === item.path;

  if (!hasChildren) {
    return (
      <li>
        <NavLink
          to={item.path}
          className={({ isActive: active }) =>
            cn(
              'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
              active
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800'
            )
          }
          onClick={onNavigate}
          aria-current={isActive ? 'page' : undefined}
        >
          {renderIcon(item.icon)}
          <span className="truncate">{item.label}</span>
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onToggleSubmenu(isOpen ? null : item.path)}
        className={cn(
          'flex w-full items-center justify-between gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500',
          isActive || isParentActive
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800'
        )}
        aria-expanded={isOpen}
        aria-controls={`${item.id}-submenu`}
        id={`${item.id}-trigger`}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          {renderIcon(item.icon)}
          <span className="truncate">{item.label}</span>
        </span>
        <icons.ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 text-neutral-500 transition-transform',
            isOpen && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <ul
          id={`${item.id}-submenu`}
          role="menu"
          className="mt-1 space-y-0.5 ml-7 border-l border-neutral-200 pl-3 dark:border-neutral-700"
        >
          {item.children?.map((child) => (
            <li key={child.id}>
              <NavLink
                to={child.path}
                className={({ isActive: active }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800',
                    active && 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  )
                }
                onClick={onNavigate}
                role="menuitem"
                aria-current={isActivePath(child.path) ? 'page' : undefined}
              >
                {renderIcon(child.icon, 'h-3.5 w-3.5 flex-shrink-0 text-neutral-400')}
                <span className="truncate">{child.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}